import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import EventOutlinedIcon from "@mui/icons-material/EventOutlined"
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Select from "@mui/material/Select"
import Typography from "@mui/material/Typography"
import { addDays, format } from "date-fns"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminLiquidToolbar from "../components/AdminLiquidToolbar"
import AdminPageHeader from "../components/AdminPageHeader"
import StatCard from "../components/StatCard"
import MeetingsBookButton from "./MeetingsBookButton"
import {
  browserTimeZone,
  getApiErrorMessage,
  providerLabel,
} from "./meetingUtils"

export const ANALYTICS_RANGES = [
  { value: "this_month", label: "This month" },
  { value: "last_30", label: "Last 30 days" },
  { value: "next_30", label: "Next 30 days" },
]

// An empty object means "the current month", which is the API default.
export function analyticsRangeParams(range, today = new Date()) {
  const day = (offset) => format(addDays(today, offset), "yyyy-MM-dd")
  if (range === "last_30") return { start: day(-30), end: day(0) }
  if (range === "next_30") return { start: day(0), end: day(30) }
  return {}
}

function CountBars({ rows, getLabel }) {
  const max = Math.max(1, ...rows.map((row) => row.total))
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No meetings in this range.
      </Typography>
    )
  }
  return (
    <Column gap={1}>
      {rows.map((row) => (
        <Row key={getLabel(row)} sx={{ alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2" sx={{ width: 140, flexShrink: 0 }}>
            {getLabel(row)}
          </Typography>
          <Box sx={{ flex: 1, bgcolor: "action.hover", borderRadius: 1 }}>
            <Box
              sx={{
                width: `${(row.total / max) * 100}%`,
                minWidth: 4,
                height: 12,
                borderRadius: 1,
                bgcolor: "primary.main",
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ width: 32, textAlign: "right" }}>
            {row.total}
          </Typography>
        </Row>
      ))}
    </Column>
  )
}

CountBars.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({ total: PropTypes.number }))
    .isRequired,
  getLabel: PropTypes.func.isRequired,
}

export default function MeetingsAnalyticsPage() {
  const [range, setRange] = useState("this_month")
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setErrorMessage("")
    adminApi
      .fetchMeetingAnalytics({
        ...analyticsRangeParams(range),
        timezone: browserTimeZone(),
      })
      .then(({ data: payload }) => {
        if (!cancelled) setData(payload)
      })
      .catch((error) => {
        if (!cancelled)
          setErrorMessage(
            getApiErrorMessage(error, "Could not load the meeting analytics.")
          )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [range, refreshKey])

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Meeting analytics"
        subtitle="Completed means the meeting time has passed."
        actions={
          <MeetingsBookButton
            onBooked={() => setRefreshKey((key) => key + 1)}
          />
        }
      />
      <AdminLiquidToolbar>
        <Select
          size="small"
          value={range}
          onChange={(event) => setRange(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {ANALYTICS_RANGES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" color="text.secondary">
          {data ? `${data.total} meetings in range` : ""}
        </Typography>
      </AdminLiquidToolbar>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Upcoming"
            value={data?.upcoming ?? 0}
            icon={EventOutlinedIcon}
            accent="info"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Completed"
            value={data?.completed ?? 0}
            icon={CheckCircleOutlineIcon}
            accent="success"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Rescheduled"
            value={data?.rescheduled ?? 0}
            icon={EventRepeatOutlinedIcon}
            accent="warning"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Cancelled"
            value={data?.cancelled ?? 0}
            icon={CancelOutlinedIcon}
            accent="danger"
            loading={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Meetings per day
            </Typography>
            <CountBars rows={data?.by_day || []} getLabel={(row) => row.date} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Column gap={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                By user
              </Typography>
              <CountBars
                rows={data?.by_user || []}
                getLabel={(row) => row.name}
              />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                By calendar
              </Typography>
              <CountBars
                rows={data?.by_provider || []}
                getLabel={(row) => providerLabel(row.provider)}
              />
            </Paper>
          </Column>
        </Grid>
      </Grid>
    </Column>
  )
}
